import { ClearOutlined } from '@ant-design/icons';
import { USER_ROLES, USER_ROLE_VALUES, type UserRole, canAdminister } from '@cult/domain';
import {
  type AdminUser,
  type AdminUserListQuery,
  SORT_ORDERS,
  USER_SORTS,
  type UserOrder,
  type UserSort,
} from '@cult/schemas';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Button, Flex, Input, Segmented, Skeleton, Space, Switch, Typography } from 'antd';
import { useMemo, useState } from 'react';
import {
  BlankState,
  ExportCsvButton,
  FilterRow,
  FilterRowSpacer,
  InlineError,
  PageHeader,
  useTablePagination,
} from '../../../components';
import { evaluateModeratorAccess, requireAdminAccess } from '../../../features/admin';
import { EditUserModal, UsersTable } from '../../../features/admin/users/components';
import { buildUserParams, useAdminUsers } from '../../../features/admin/users/hooks';
import { useAuthMe } from '../../../features/auth';

const { Text } = Typography;

type Search = {
  role?: UserRole;
  q?: string;
  hideInactive?: boolean;
};

type RoleSegment = 'all' | UserRole;

const UsersPage = () => {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const auth = useAuthMe();
  const [editing, setEditing] = useState<AdminUser | null>(null);
  const [searchInput, setSearchInput] = useState(search.q ?? '');
  const [sort, setSort] = useState<UserSort>(USER_SORTS.JOINED);
  const [order, setOrder] = useState<UserOrder>(SORT_ORDERS.DESC);

  const filters: AdminUserListQuery = useMemo(() => {
    const f: AdminUserListQuery = { includeBalance: true, sort, order };
    if (search.role) f.role = [search.role];
    if (search.hideInactive) f.isActive = true;
    if (search.q) f.q = search.q;
    return f;
  }, [search.role, search.hideInactive, search.q, sort, order]);

  const handleSortChange = (nextSort: UserSort, nextOrder: UserOrder) => {
    setSort(nextSort);
    setOrder(nextOrder);
  };

  const { page, pageSize, buildPagination } = useTablePagination(JSON.stringify(filters));
  const users = useAdminUsers(filters, page, pageSize);
  const segment: RoleSegment = search.role ?? 'all';
  const hasActiveFilters = Boolean(search.role || search.q || search.hideInactive);

  const setSegment = (v: RoleSegment) => {
    navigate({
      to: '/admin/users',
      search: (prev: Search) => ({ ...prev, role: v === 'all' ? undefined : v }),
    });
  };

  const setHideInactive = (checked: boolean) => {
    navigate({
      to: '/admin/users',
      search: (prev: Search) => ({ ...prev, hideInactive: checked || undefined }),
    });
  };

  const applySearch = (value: string) => {
    setSearchInput(value);
    navigate({
      to: '/admin/users',
      search: (prev: Search) => ({ ...prev, q: value.trim() || undefined }),
    });
  };

  const clearFilters = () => {
    setSearchInput('');
    navigate({ to: '/admin/users', search: () => ({}) });
  };

  const all = users.data?.users ?? [];
  const currentUserRole: UserRole = auth.data?.role ?? USER_ROLES.MEMBER;

  return (
    <Flex vertical gap="large">
      <PageHeader
        title="Users"
        subtitle="Manage who can sign in and who has admin privileges."
        action={
          // Moderators can open this page but not export it (the API enforces the same).
          canAdminister(currentUserRole) ? (
            <ExportCsvButton
              path="/admin/users/export"
              params={buildUserParams({ ...filters, includeBalance: undefined })}
              fileBaseName="users"
            />
          ) : null
        }
      />

      <FilterRow>
        <Segmented<RoleSegment>
          value={segment}
          onChange={(v) => setSegment(v as RoleSegment)}
          options={[
            { value: 'all', label: 'All' },
            { value: USER_ROLES.ADMIN, label: 'Admins' },
            { value: USER_ROLES.MODERATOR, label: 'Moderators' },
            { value: USER_ROLES.IDEA_MANAGER, label: 'Idea managers' },
            { value: USER_ROLES.MEMBER, label: 'Members' },
          ]}
        />
        <Input.Search
          placeholder="Search by name or email"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onSearch={applySearch}
          allowClear
          style={{ width: 280 }}
        />
        <Space size={8}>
          <Switch checked={!!search.hideInactive} onChange={setHideInactive} size="small" />
          <Text type="secondary" style={{ fontSize: 13 }}>
            Hide deactivated
          </Text>
        </Space>
        <FilterRowSpacer />
        {hasActiveFilters ? (
          <Button type="text" size="small" icon={<ClearOutlined />} onClick={clearFilters}>
            Reset filters
          </Button>
        ) : null}
      </FilterRow>

      {users.isError ? <InlineError title="Couldn't load users" detail={users.error.message} /> : null}

      {users.isPending ? (
        <Skeleton active paragraph={{ rows: 6 }} />
      ) : all.length === 0 && !users.isError ? (
        <BlankState
          title={hasActiveFilters ? 'No users match these filters' : 'No users yet'}
          hint={
            hasActiveFilters
              ? 'Try widening the role filter or clearing the search.'
              : 'Once people sign in, they will show up here.'
          }
          action={
            hasActiveFilters ? (
              <Button size="small" onClick={clearFilters}>
                Clear filters
              </Button>
            ) : null
          }
        />
      ) : all.length === 0 ? null : (
        <UsersTable
          users={all}
          loading={users.isFetching}
          currentUserId={auth.data?.id ?? ''}
          currentUserRole={currentUserRole}
          onEdit={setEditing}
          sort={sort}
          order={order}
          onSortChange={handleSortChange}
          defaultSort={USER_SORTS.JOINED}
          defaultOrder={SORT_ORDERS.DESC}
          pagination={buildPagination(users.data?.total ?? 0)}
        />
      )}

      <EditUserModal user={editing} currentUserRole={currentUserRole} onClose={() => setEditing(null)} />
    </Flex>
  );
};

export const Route = createFileRoute('/_auth/admin/users')({
  beforeLoad: requireAdminAccess(evaluateModeratorAccess),
  validateSearch: (search: Record<string, unknown>): Search => ({
    role:
      typeof search.role === 'string' && (USER_ROLE_VALUES as readonly string[]).includes(search.role)
        ? (search.role as UserRole)
        : undefined,
    q: typeof search.q === 'string' ? search.q : undefined,
    hideInactive: search.hideInactive === true || search.hideInactive === 'true' ? true : undefined,
  }),
  component: UsersPage,
});
